import { z } from "zod";
import {
  AI_FEATURES,
  AI_PROVIDERS,
  AD_PLATFORMS,
  CAMPAIGN_OBJECTIVES,
  CHANNELS,
  FOLLOWUP_TYPES,
  PAYMENT_METHODS,
  type AiFeature,
  type AiProvider,
} from "../enums";
import type { AiModelTier } from "../ai-models";

// AI layer contracts shared by the API and the web app: settings (organization and platform), usage, assistant,
// content generation, Business Brain (profile, knowledge, playground) and insights.

const objectIdString = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const text = (max: number) => z.string().trim().max(max, `${max} caractères maximum`);
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");

export const aiModelId = z
  .string()
  .trim()
  .min(2, "Modèle invalide")
  .max(120, "Modèle invalide")
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, "Modèle invalide");

export const aiApiKeyInput = z
  .string()
  .trim()
  .min(10, "Clé API trop courte")
  .max(500, "Clé API trop longue")
  .regex(/^\S+$/, "La clé API ne doit pas contenir d'espaces");

export const aiTokenLimit = z.number().int("Nombre entier attendu").min(0, "Valeur invalide").max(100_000_000_000, "Valeur trop grande");

export const AI_PERIOD_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

// ---------------------------------------------------------------------------------------------------------------
// Organization settings, models, usage

export const aiFeatureTogglesSchema = z
  .object({
    assistant: z.boolean(),
    autoreply: z.boolean(),
    media_buyer: z.boolean(),
    insights: z.boolean(),
    content: z.boolean(),
    brain_playground: z.boolean(),
    recommendations: z.boolean(),
  })
  .partial();

export const aiOrgSettingsUpdateSchema = z.object({
  mode: z.enum(["platform", "byok"]).optional(),
  provider: z.enum(AI_PROVIDERS).nullable().optional(),
  model: aiModelId.nullable().optional(),
  /** New organization key (BYOK). null removes the stored key. */
  apiKey: aiApiKeyInput.nullable().optional(),
  defaultMemberMonthlyLimit: aiTokenLimit.nullable().optional(),
  features: aiFeatureTogglesSchema.optional(),
});
export type AiOrgSettingsUpdate = z.infer<typeof aiOrgSettingsUpdateSchema>;

export const aiSettingsTestSchema = z.object({
  provider: z.enum(AI_PROVIDERS).optional(),
  apiKey: aiApiKeyInput.optional(),
  model: aiModelId.optional(),
});
export type AiSettingsTestInput = z.infer<typeof aiSettingsTestSchema>;

export const aiModelsQuery = z.object({ provider: z.enum(AI_PROVIDERS) });
export const aiUsageQuery = z.object({ period: z.string().regex(AI_PERIOD_REGEX, "Période invalide").optional() });

// ---------------------------------------------------------------------------------------------------------------
// Platform administration

export const aiAdminProviderUpdateSchema = z.object({
  apiKey: aiApiKeyInput.nullable().optional(),
  enabled: z.boolean().optional(),
  enabledModels: z.array(aiModelId).max(100).optional(),
});

export const aiAdminProvidersUpdateSchema = z.object({
  providers: z
    .object({
      anthropic: aiAdminProviderUpdateSchema.optional(),
      openai: aiAdminProviderUpdateSchema.optional(),
      gemini: aiAdminProviderUpdateSchema.optional(),
    })
    .optional(),
  defaultProvider: z.enum(AI_PROVIDERS).nullable().optional(),
  defaultModel: aiModelId.nullable().optional(),
  featureModels: z
    .array(z.object({ feature: z.enum(AI_FEATURES), provider: z.enum(AI_PROVIDERS), model: aiModelId }))
    .max(AI_FEATURES.length)
    .optional(),
  byokAllowedByDefault: z.boolean().optional(),
});
export type AiAdminProvidersUpdate = z.infer<typeof aiAdminProvidersUpdateSchema>;

export const aiAdminValidateSchema = z.object({ provider: z.enum(AI_PROVIDERS), apiKey: aiApiKeyInput.optional() });
export const aiAdminOrgLimitSchema = z.object({ monthlyTokenLimit: aiTokenLimit.nullable() });

// ---------------------------------------------------------------------------------------------------------------
// Assistant

export const assistantMessageSchema = z.object({
  threadId: objectIdString.optional(),
  message: z.string().trim().min(1, "Message vide").max(4000, "Message trop long (4 000 caractères maximum)"),
});
export type AssistantMessageInput = z.infer<typeof assistantMessageSchema>;

export const ASSISTANT_TOOL_LABELS: Record<string, string> = {
  sales_summary: "Résumé des ventes",
  top_products: "Meilleurs produits",
  orders_by_status: "Commandes par statut",
  delivery_stats: "Statistiques de livraison",
  campaign_performance: "Performance des campagnes",
  customers_stats: "Statistiques clients",
  conversations_stats: "Statistiques des conversations",
  low_stock_products: "Stocks faibles",
  search_orders: "Recherche de commandes",
  search_customers: "Recherche de clients",
};

// ---------------------------------------------------------------------------------------------------------------
// Content generation

export const AI_GENERATE_KINDS = ["product_description", "ad_copy", "landing_section", "seo_meta", "whatsapp_template", "reply_suggestion"] as const;
export type AiGenerateKind = (typeof AI_GENERATE_KINDS)[number];

export const CONTENT_TONES = ["friendly", "professional", "premium", "energetic", "playful"] as const;
export type ContentTone = (typeof CONTENT_TONES)[number];

export const CONTENT_LANGUAGES = ["fr", "ar", "en"] as const;
export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number];

export const LANDING_SECTIONS = ["hero", "benefits", "features", "offer", "guarantee", "delivery", "about", "cta"] as const;
export type LandingSection = (typeof LANDING_SECTIONS)[number];

export const AI_GENERATE_KIND_LABELS: Record<AiGenerateKind, string> = {
  product_description: "Description produit",
  ad_copy: "Texte publicitaire",
  landing_section: "Section de landing page",
  seo_meta: "Balises SEO",
  whatsapp_template: "Modèle WhatsApp",
  reply_suggestion: "Suggestion de réponse",
};

export const CONTENT_TONE_LABELS: Record<ContentTone, string> = {
  friendly: "Chaleureux",
  professional: "Professionnel",
  premium: "Premium",
  energetic: "Dynamique",
  playful: "Décalé",
};

export const LANDING_SECTION_LABELS: Record<LandingSection, string> = {
  hero: "Bannière principale",
  benefits: "Bénéfices",
  features: "Caractéristiques",
  offer: "Offre",
  guarantee: "Garantie",
  delivery: "Livraison et paiement",
  about: "À propos",
  cta: "Appel à l'action",
};

const toneField = z.enum(CONTENT_TONES).optional();
const languageField = z.enum(CONTENT_LANGUAGES).optional();

export const aiGenerateSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("product_description"),
    input: z
      .object({
        productId: objectIdString.optional(),
        name: text(200).optional(),
        category: text(80).optional(),
        notes: z.string().max(20_000).optional(),
        priceMinor: minor.nullable().optional(),
        currency: z.string().length(3).optional(),
        audience: text(300).optional(),
        tone: toneField,
        language: languageField,
      })
      .refine((v) => Boolean(v.productId || v.name), "Indiquez un produit ou un nom de produit"),
  }),
  z.object({
    kind: z.literal("ad_copy"),
    input: z
      .object({
        productId: objectIdString.optional(),
        productName: text(200).optional(),
        offer: text(300).optional(),
        audience: text(300).optional(),
        platform: z.enum(AD_PLATFORMS).default("meta"),
        objective: z.enum(CAMPAIGN_OBJECTIVES).optional(),
        count: z.number().int().min(1).max(5).default(3),
        tone: toneField,
        language: languageField,
      })
      .refine((v) => Boolean(v.productId || v.productName || v.offer), "Indiquez un produit ou une offre"),
  }),
  z.object({
    kind: z.literal("landing_section"),
    input: z.object({
      section: z.enum(LANDING_SECTIONS),
      productId: objectIdString.optional(),
      productName: text(200).optional(),
      offer: text(300).optional(),
      audience: text(300).optional(),
      tone: toneField,
      language: languageField,
    }),
  }),
  z.object({
    kind: z.literal("seo_meta"),
    input: z
      .object({
        productId: objectIdString.optional(),
        pageTitle: text(200).optional(),
        content: z.string().max(8000).optional(),
        keywords: z.array(text(60)).max(10).optional(),
        language: languageField,
      })
      .refine((v) => Boolean(v.productId || v.pageTitle || v.content), "Indiquez un produit, un titre ou un contenu"),
  }),
  z.object({
    kind: z.literal("whatsapp_template"),
    input: z.object({
      purpose: z.enum([...FOLLOWUP_TYPES, "general"]),
      context: text(1000).optional(),
      tone: toneField,
      language: languageField,
    }),
  }),
  z.object({
    kind: z.literal("reply_suggestion"),
    input: z
      .object({
        conversationId: objectIdString.optional(),
        messages: z
          .array(z.object({ from: z.enum(["customer", "business"]), text: z.string().trim().min(1).max(2000) }))
          .max(30)
          .optional(),
        customerName: text(120).optional(),
        instruction: text(500).optional(),
      })
      .refine((v) => Boolean(v.conversationId || v.messages?.length), "Indiquez une conversation ou des messages"),
  }),
]);
export type AiGenerateInput = z.infer<typeof aiGenerateSchema>;

/** Variables allowed in generated WhatsApp templates ({{name}} placeholders rendered by renderTemplate). */
export const TEMPLATE_VARIABLES = [
  "customer_name",
  "order_number",
  "order_total",
  "product_name",
  "tracking_number",
  "tracking_url",
  "delivery_date",
  "store_name",
  "payment_link",
] as const;

export type AiGenerateResult =
  | { kind: "product_description"; text: string; shortText: string; model: string; provider: AiProvider }
  | {
      kind: "ad_copy";
      variants: Array<{ primaryText: string; headline: string; description: string }>;
      /** First variant, flat, for single-copy callers. */
      text: string;
      headline: string;
      description: string;
      model: string;
      provider: AiProvider;
    }
  | { kind: "landing_section"; title: string; subtitle: string; bullets: string[]; cta: string; model: string; provider: AiProvider }
  | { kind: "seo_meta"; title: string; description: string; model: string; provider: AiProvider }
  | { kind: "whatsapp_template"; body: string; variables: string[]; model: string; provider: AiProvider }
  | { kind: "reply_suggestion"; text: string; model: string; provider: AiProvider };

// ---------------------------------------------------------------------------------------------------------------
// Business Brain: profile, knowledge, playground

const audienceSchema = z.object({
  ageMin: z.number().int().min(13).max(100),
  ageMax: z.number().int().min(13).max(100),
  genders: z.array(z.enum(["male", "female"])).max(2),
  interests: z.array(text(80).min(1)).max(50),
  locations: z.array(text(80).min(1)).max(50),
  description: text(1500),
});

export const BRAND_LANGUAGES = ["fr", "ar", "ar_tn", "en"] as const;
export type BrandLanguage = (typeof BRAND_LANGUAGES)[number];

export const BRAND_LANGUAGE_LABELS: Record<BrandLanguage, string> = {
  fr: "Français",
  ar: "Arabe",
  ar_tn: "Darija tunisienne",
  en: "Anglais",
};

export const businessProfileUpdateSchema = z
  .object({
    businessName: text(160),
    industry: text(120),
    description: text(3000),
    website: text(300),
    productsSummary: text(3000),
    targetMarkets: z.array(z.string().trim().length(2).toUpperCase()).max(20),
    targetAudience: audienceSchema.partial(),
    usp: z.array(text(200).min(1)).max(15),
    brandVoice: z
      .object({
        tone: text(120),
        formality: z.enum(["tu", "vous"]),
        languages: z.array(z.enum(BRAND_LANGUAGES)).max(4),
        doNotSay: z.array(text(120).min(1)).max(30),
        signature: text(200),
      })
      .partial(),
    economics: z
      .object({
        averageOrderValueMinor: minor.nullable(),
        grossMarginPct: z.number().min(0).max(100).nullable(),
        expectedDeliveryRatePct: z.number().min(0).max(100).nullable(),
        avgShippingCostMinor: minor.nullable(),
        rtoCostMinor: minor.nullable(),
        targetRoas: z.number().min(0).max(100).nullable(),
        targetCpaMinor: minor.nullable(),
        monthlyAdBudgetMinor: minor.nullable(),
      })
      .partial(),
    delivery: z
      .object({
        zonesText: text(2000),
        delaysText: text(1000),
        feesText: text(1000),
        freeShippingThresholdMinor: minor.nullable(),
      })
      .partial(),
    payment: z.object({ methods: z.array(z.enum(PAYMENT_METHODS)).max(PAYMENT_METHODS.length), notes: text(1000) }).partial(),
    returnPolicy: text(3000),
    openingHours: text(500),
    contact: z
      .object({
        phone: text(40),
        whatsapp: text(40),
        email: z.union([z.literal(""), z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(254)]),
        address: text(300),
      })
      .partial(),
    faqs: z
      .array(
        z.object({
          id: objectIdString.optional(),
          question: z.string().trim().min(3, "Question trop courte").max(300),
          answer: z.string().trim().min(1, "Réponse requise").max(2000),
        }),
      )
      .max(100),
    competitors: z.array(text(120).min(1)).max(30),
    seasonality: text(1000),
  })
  .partial()
  .refine((v) => !v.targetAudience?.ageMin || !v.targetAudience?.ageMax || v.targetAudience.ageMin <= v.targetAudience.ageMax, {
    message: "L'âge minimum doit être inférieur à l'âge maximum",
    path: ["targetAudience", "ageMin"],
  });
export type BusinessProfileUpdate = z.infer<typeof businessProfileUpdateSchema>;

export interface BusinessProfileDto {
  id: string;
  businessName: string;
  industry: string;
  description: string;
  website: string;
  productsSummary: string;
  targetMarkets: string[];
  targetAudience: { ageMin: number; ageMax: number; genders: string[]; interests: string[]; locations: string[]; description: string };
  usp: string[];
  brandVoice: { tone: string; formality: "tu" | "vous"; languages: string[]; doNotSay: string[]; signature: string };
  economics: {
    averageOrderValueMinor: number | null;
    grossMarginPct: number | null;
    expectedDeliveryRatePct: number | null;
    avgShippingCostMinor: number | null;
    rtoCostMinor: number | null;
    targetRoas: number | null;
    targetCpaMinor: number | null;
    monthlyAdBudgetMinor: number | null;
  };
  delivery: { zonesText: string; delaysText: string; feesText: string; freeShippingThresholdMinor: number | null };
  payment: { methods: string[]; notes: string };
  returnPolicy: string;
  openingHours: string;
  contact: { phone: string; whatsapp: string; email: string; address: string };
  faqs: Array<{ id: string; question: string; answer: string }>;
  competitors: string[];
  seasonality: string;
  currency: string;
  updatedAt: string | null;
}

export const KNOWLEDGE_KINDS = ["faq", "policy", "product_note", "snippet", "learned"] as const;
export type KnowledgeKind = (typeof KNOWLEDGE_KINDS)[number];

export const KNOWLEDGE_KIND_LABELS: Record<KnowledgeKind, string> = {
  faq: "Question fréquente",
  policy: "Politique",
  product_note: "Note produit",
  snippet: "Information",
  learned: "Appris des conversations",
};

export const knowledgeItemInputSchema = z.object({
  kind: z.enum(KNOWLEDGE_KINDS).default("snippet"),
  title: z.string().trim().min(2, "Titre trop court").max(200, "200 caractères maximum"),
  content: z.string().trim().min(1, "Contenu requis").max(8000, "8 000 caractères maximum"),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  enabled: z.boolean().default(true),
});
export type KnowledgeItemInput = z.infer<typeof knowledgeItemInputSchema>;

export const knowledgeItemUpdateSchema = knowledgeItemInputSchema.partial();
export type KnowledgeItemUpdate = z.infer<typeof knowledgeItemUpdateSchema>;

export const knowledgeListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(120).optional(),
  kind: z.enum(KNOWLEDGE_KINDS).optional(),
});
export type KnowledgeListQuery = z.infer<typeof knowledgeListQuery>;

export interface KnowledgeItemDto {
  id: string;
  kind: KnowledgeKind;
  title: string;
  content: string;
  tags: string[];
  source: "manual" | "conversation" | "import" | "ai";
  enabled: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export const SALES_STAGES = ["idle", "qualifying", "collecting", "confirming", "confirmed", "handoff"] as const;
export type SalesStage = (typeof SALES_STAGES)[number];
export const SALES_INTENTS = ["question", "order", "complaint", "greeting", "tracking", "human", "other"] as const;
export type SalesIntent = (typeof SALES_INTENTS)[number];

export const SALES_STAGE_LABELS: Record<SalesStage, string> = {
  idle: "En attente",
  qualifying: "Découverte du besoin",
  collecting: "Collecte des informations",
  confirming: "Confirmation demandée",
  confirmed: "Commande confirmée",
  handoff: "Transfert à un humain",
};

export const SALES_INTENT_LABELS: Record<SalesIntent, string> = {
  question: "Question",
  order: "Commande",
  complaint: "Réclamation",
  greeting: "Salutation",
  tracking: "Suivi de commande",
  human: "Demande d'un conseiller",
  other: "Autre",
};

export const brainPlaygroundSchema = z
  .object({
    messages: z
      .array(z.object({ from: z.enum(["customer", "business"]), text: z.string().trim().min(1, "Message vide").max(2000) }))
      .min(1, "Ajoutez au moins un message")
      .max(40, "40 messages maximum"),
    customerName: text(120).optional(),
    channel: z.enum(CHANNELS).default("whatsapp"),
    previousState: z
      .object({ stage: z.enum(SALES_STAGES).optional(), draftOrder: z.unknown().optional(), summary: text(2000).optional() })
      .nullable()
      .optional(),
  })
  .refine((v) => v.messages[v.messages.length - 1]?.from === "customer", { message: "Le dernier message doit venir du client", path: ["messages"] });
export type BrainPlaygroundInput = z.infer<typeof brainPlaygroundSchema>;

export interface SalesQuoteDto {
  currency: string;
  lines: Array<{ productId: string; variantId: string | null; name: string; variantTitle: string; qty: number; unitPriceMinor: number; totalMinor: number; offerLabel: string | null }>;
  subtotalMinor: number;
  shippingFeeMinor: number;
  totalMinor: number;
}

export interface SalesAgentDecisionDto {
  reply: string;
  intent: SalesIntent;
  stage: SalesStage;
  orderDraft: {
    items: Array<{ productId: string; variantId: string | null; qty: number }>;
    customer: { name?: string; phone?: string; governorate?: string; city?: string; address?: string };
  } | null;
  readyToConfirm: boolean;
  customerConfirmed: boolean;
  handoff: boolean;
  handoffReason?: string;
  tags: string[];
  viewedProductIds: string[];
  summary: string;
  quote?: SalesQuoteDto | null;
  issues?: string[];
  missingFields?: string[];
}

export interface PlaygroundResultDto {
  decision: SalesAgentDecisionDto;
  model: string;
  latencyMs: number;
}

export interface BrainCompletenessItem {
  key: string;
  label: string;
  done: boolean;
  weight: number;
  hint: string;
  link: string;
}

export interface BrainCompletenessDto {
  score: number;
  items: BrainCompletenessItem[];
  missing: string[];
}

export interface BrainSummaryDto {
  business: { name: string; industry: string; description: string; tone: string; formality: "tu" | "vous"; languages: string[] };
  catalog: {
    activeProducts: number;
    withVariants: number;
    quantityOffers: number;
    upsells: number;
    outOfStock: number;
    priceRange: { minMinor: number; maxMinor: number } | null;
    currency: string;
    sample: Array<{ id: string; name: string; priceMinor: number }>;
  };
  delivery: { marketCode: string; defaultFeeMinor: number; zonesWithFees: number; freeShippingThresholdMinor: number | null; codEnabled: boolean; delaysText: string };
  policies: { returnPolicy: boolean; openingHours: boolean; paymentMethods: string[]; faqs: number };
  knowledge: { total: number; enabled: number; byKind: Record<string, number> };
  contextPreview: string;
  contextTokensEstimate: number;
}

// ---------------------------------------------------------------------------------------------------------------
// Insights

export const INSIGHT_KINDS = ["anomaly", "opportunity", "risk", "summary", "forecast"] as const;
export type InsightKind = (typeof INSIGHT_KINDS)[number];
export const INSIGHT_CATEGORIES = ["sales", "ads", "delivery", "customers", "products", "conversations", "general"] as const;
export type InsightCategory = (typeof INSIGHT_CATEGORIES)[number];
export const INSIGHT_SEVERITIES = ["info", "positive", "warning", "critical"] as const;
export type InsightSeverity = (typeof INSIGHT_SEVERITIES)[number];
export const INSIGHT_STATUSES = ["new", "read", "dismissed", "actioned"] as const;
export type InsightStatus = (typeof INSIGHT_STATUSES)[number];

export const INSIGHT_KIND_LABELS: Record<InsightKind, string> = {
  anomaly: "Anomalie",
  opportunity: "Opportunité",
  risk: "Risque",
  summary: "Synthèse",
  forecast: "Prévision",
};

export const INSIGHT_CATEGORY_LABELS: Record<InsightCategory, string> = {
  sales: "Ventes",
  ads: "Publicité",
  delivery: "Livraison",
  customers: "Clients",
  products: "Produits",
  conversations: "Conversations",
  general: "Général",
};

export const INSIGHT_SEVERITY_LABELS: Record<InsightSeverity, string> = {
  info: "Information",
  positive: "Positif",
  warning: "À surveiller",
  critical: "Critique",
};

export const insightListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(24),
  status: z.enum(["active", "new", "read", "dismissed", "actioned", "all"]).default("active"),
  severity: z.enum(INSIGHT_SEVERITIES).optional(),
  category: z.enum(INSIGHT_CATEGORIES).optional(),
  kind: z.enum(INSIGHT_KINDS).optional(),
  source: z.enum(["ai", "rules"]).optional(),
});
export type InsightListQuery = z.infer<typeof insightListQuery>;

export const insightStatusSchema = z.object({ status: z.enum(INSIGHT_STATUSES) });
export const insightRefreshSchema = z.object({ includeSummary: z.boolean().default(false) });

export interface InsightDto {
  id: string;
  kind: InsightKind;
  category: InsightCategory;
  title: string;
  summary: string;
  details: string;
  metrics: Record<string, unknown> | null;
  severity: InsightSeverity;
  period: { from: string | null; to: string | null };
  source: "ai" | "rules";
  status: InsightStatus;
  actions: Array<{ label: string; link: string }>;
  createdAt: string;
}

export interface InsightListDto {
  items: InsightDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: { new: number; active: number; bySeverity: Record<InsightSeverity, number>; byCategory: Record<InsightCategory, number> };
}

// ---------------------------------------------------------------------------------------------------------------
// DTOs of the settings, status, usage and assistant endpoints

export type AiKeySource = "platform" | "byok";

export interface AiModelOption {
  id: string;
  displayName: string;
  tier: AiModelTier | null;
  recommended: boolean;
  isDefault: boolean;
  inputUsdPerM: number | null;
  outputUsdPerM: number | null;
  contextWindow: number | null;
}

export interface AiModelListDto {
  provider: AiProvider;
  source: "live" | "catalog";
  defaultModel: string;
  items: AiModelOption[];
  warning: string | null;
}

export interface AiSettingsDto {
  mode: "platform" | "byok";
  provider: AiProvider | null;
  model: string | null;
  apiKey: { set: boolean; masked: string | null; validatedAt: string | null; lastError: string | null };
  byokAllowed: boolean;
  defaultMemberMonthlyLimit: number | null;
  monthlyTokenLimit: number | null;
  planTokenLimit: number;
  planName: string;
  features: Record<AiFeature, boolean>;
  platformProviders: Array<{ provider: AiProvider; available: boolean; enabledModels: string[] }>;
  effective: { available: boolean; reason: string | null; provider: AiProvider | null; model: string | null; keySource: AiKeySource | null };
  aiEnabled: boolean;
  updatedAt: string | null;
}

export interface AiSettingsTestResultDto {
  ok: boolean;
  provider: AiProvider;
  model: string | null;
  keySource: AiKeySource | "candidate";
  modelAvailable: boolean | null;
  modelsCount: number;
  latencyMs: number;
  message: string;
}

export interface AiStatusDto {
  aiEnabled: boolean;
  period: string;
  provider: AiProvider | null;
  model: string | null;
  keySource: AiKeySource | null;
  features: Record<AiFeature, { available: boolean; reason: string | null }>;
  org: { usedTokens: number; limit: number; pct: number };
  member: { usedTokens: number; limit: number | null; pct: number | null };
}

export interface AiUsageDto {
  period: string;
  periods: string[];
  org: { platformTokens: number; byokTokens: number; limit: number; pct: number; calls: number; errors: number; costMicroUsd: number };
  plan: { name: string; aiTokensMonthly: number; overridden: boolean };
  byFeature: Array<{ feature: AiFeature; tokens: number; calls: number; costMicroUsd: number }>;
  byMember: Array<{
    userId: string;
    membershipId: string | null;
    name: string;
    email: string;
    role: string | null;
    tokens: number;
    calls: number;
    limit: number | null;
    limitSource: "member" | "default" | null;
  }>;
  byModel: Array<{ provider: AiProvider; model: string; displayName: string; keySource: AiKeySource; tokens: number; calls: number; costMicroUsd: number }>;
  daily: Array<{ date: string; tokens: number; calls: number }>;
}

export interface AiAdminProviderDto {
  provider: AiProvider;
  configured: boolean;
  keyMasked: string | null;
  envKey: boolean;
  enabled: boolean;
  usable: boolean;
  keySource: "db" | "env" | null;
  enabledModels: string[];
  lastValidatedAt: string | null;
  lastError: string | null;
}

export interface AiAdminProvidersDto {
  providers: AiAdminProviderDto[];
  defaultProvider: AiProvider | null;
  defaultModel: string | null;
  featureModels: Array<{ feature: AiFeature; provider: AiProvider; model: string }>;
  byokAllowedByDefault: boolean;
  aiEnabled: boolean;
  env: { defaultProvider: AiProvider | null; defaultModel: string | null };
}

export interface AiAdminUsageDto {
  period: string;
  totals: { tokens: number; platformTokens: number; byokTokens: number; calls: number; errors: number; costMicroUsd: number };
  byProvider: Array<{ provider: AiProvider; tokens: number; calls: number; costMicroUsd: number }>;
  organizations: Array<{
    orgId: string;
    name: string;
    platformTokens: number;
    byokTokens: number;
    calls: number;
    costMicroUsd: number;
    planLimit: number;
    overrideLimit: number | null;
    pct: number;
  }>;
}

export interface AssistantThreadSummaryDto {
  id: string;
  title: string;
  preview: string;
  messagesCount: number;
  lastMessageAt: string;
}

export interface AssistantMessageDto {
  role: "user" | "assistant";
  content: string;
  toolsUsed: string[];
  createdAt: string;
}

export interface AssistantThreadDto {
  id: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
  messages: AssistantMessageDto[];
}

export interface AssistantReplyDto {
  thread: AssistantThreadSummaryDto;
  message: AssistantMessageDto;
  usage: { inputTokens: number; outputTokens: number; cachedTokens: number };
  provider: AiProvider;
  model: string;
}
